import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Slider } from "@/components/ui/slider";
import { money, num, pct, SemaforoChip } from "./faUtils";

const ROWS = [
  ["ingreso_total", "Ingreso total", "money"],
  ["ebitda", "EBITDA", "money"],
  ["margen_ebitda_pct", "Margen EBITDA", "pct"],
  ["utilidad_neta", "Utilidad Neta", "money"],
  ["costo_por_ml", "Costo $/ML", "money"],
  ["break_even_ml", "Break-even (ML/mes)", "num"],
  ["fcf", "Flujo libre (FCF)", "money"],
  ["fcfe", "FCF al dueño", "money"],
];

const fmt = (t, v) => (t === "pct" ? pct(v) : t === "num" ? num(v) : money(v));

function Ctl({ label, value, onChange, min, max, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</p>
        <span className={`font-heading font-black text-lg ${value < 0 ? "text-red-700" : value > 0 ? "text-emerald-700" : "text-slate-500"}`}>
          {value > 0 ? "+" : ""}{value}%
        </span>
      </div>
      <Slider value={[value]} min={min} max={max} step={1} onValueChange={([v]) => onChange(v)} data-testid={testid} />
    </div>
  );
}

export default function FaSensitivity({ year }) {
  const [price, setPrice] = useState(-10);
  const [diesel, setDiesel] = useState(15);
  const [ml, setMl] = useState(-10);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setBusy(true);
      api.get(`/finanalysis/sensitivity/${year}`, { params: { price_pct: price, diesel_pct: diesel, ml_pct: ml } })
        .then((r) => setRes(r.data))
        .catch((e) => toast.error(apiError(e)))
        .finally(() => setBusy(false));
    }, 350);
    return () => clearTimeout(t);
  }, [year, price, diesel, ml]);

  return (
    <div className="space-y-4 pt-3">
      <p className="text-sm text-slate-500">
        Escenario &ldquo;¿qué pasa si…?&rdquo; sobre el año {year} (usa el real donde existe y el budget donde no).
        Los costos variables escalan con el avance; el diésel recibe además su propio choque.
      </p>
      <div className="grid md:grid-cols-3 gap-4">
        <Ctl label="Precio por ML" value={price} onChange={setPrice} min={-30} max={30} testid="fa-sens-price" />
        <Ctl label="Costo Diésel" value={diesel} onChange={setDiesel} min={-30} max={50} testid="fa-sens-diesel" />
        <Ctl label="Avance ML" value={ml} onChange={setMl} min={-30} max={30} testid="fa-sens-ml" />
      </div>

      {res && (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto" data-testid="fa-sens-table">
          <div className="flex items-center justify-between px-4 pt-3">
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Impacto anual {busy ? "· recalculando…" : ""}</p>
            <SemaforoChip value={res.semaforo_shocked} testid="fa-sens-semaforo" />
          </div>
          <table className="w-full text-sm mt-2">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-slate-400 border-b border-slate-200">
                <th className="text-left py-2 px-4">Indicador</th>
                <th className="text-right px-4">Base</th>
                <th className="text-right px-4">Escenario</th>
                <th className="text-right px-4">Δ Impacto</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map(([k, l, t]) => {
                const d = res.delta[k] || 0;
                const bad = ["costo_por_ml", "break_even_ml"].includes(k) ? d > 0 : d < 0;
                return (
                  <tr key={k} className="border-b border-slate-100">
                    <td className="py-2 px-4 text-slate-600 font-medium">{l}</td>
                    <td className="text-right px-4 tabular-nums text-slate-500">{fmt(t, res.base[k])}</td>
                    <td className="text-right px-4 tabular-nums font-bold text-slate-900">{fmt(t, res.shocked[k])}</td>
                    <td className={`text-right px-4 tabular-nums font-bold ${bad ? "text-red-700" : "text-emerald-700"}`}>
                      {d > 0 ? "+" : ""}{fmt(t, d)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
